import { RATE_TYPES } from "./rateTypes";

type PlanType = "ppo" | "hmo" | "epo" | "pos" | "indemnity";

type GroupInformation = {
  groupName: string;
  groupNumber: string;
  groupAddress: string;
  fein: string;
};

type GroupContact = {
  name: string;
  isPrimary: boolean;
  workEmail: string;
  workPhone: string;
  role: string;
};

type Billing = {
  identifier: string;
  name: string;
  description: string;
};

type BenefitClass = {
  coverageEffectiveDate: string;
  identifier: string;
  name: string;
  minWeeklyEligibleHours: string;
};

type Coverage = {
  name:
    | "Medical"
    | "Dental"
    | "Voluntary Life"
    | "Basic Life"
    | "Vision"
    | "LTD"
    | "STD"
    | "Accident"
    | "Hospital Indemnity"
    | "Critical Illness";
  renewalDate: string | null;
  originalEffectiveDate: string | null;
  mostRecentEffectiveDate: string | null;
  rateGuarantee: string | null;
  fundingType: string | null;
  cobraEligibility: string | null;
};

type MedicalCoverage = Coverage & {
  plans: MedicalPlan[];
};

type DentalCoverage = Coverage & {
  plans: DentalPlan[];
};

type VisionCoverage = Coverage & {
  plans: VisionPlan[];
};

type StdCoverage = Coverage & {
  plans: StdPlan[];
};

type LtdCoverage = Coverage & {
  plans: LtdPlan[];
};

type BasicLifeCoverage = Coverage & {
  plans: BasicLifePlan[];
};

type VoluntaryLifeCoverage = Coverage & {
  plans: VoluntaryLifePlan[];
};

type CriticalIllnessCoverage = Coverage & {
  plans: CriticalIllnessPlan[];
};

type HospitalIndemnityCoverage = Coverage & {
  plans: HospitalIndemnityPlan[];
};

type AccidentCoverage = Coverage & {
  plans: AccidentPlan[];
};

type PlanRates =
  | CompositeRate
  | FlatRate
  | AgeBandedTobaccoRate
  | AgeBandedRate
  | AgeBandedGenderRate
  | BasicLifeRate
  | VolLifeRate
  | CriticalIllnessRate
  | AgeBandedTieredRate;

type Plan = {
  planType: PlanType | null;
  planIdentifier: string;
  planName: string;
  generalInformation: GeneralInformation;
  basics: {
    cobraElligible: string;
    metallicLevel: string;
  };
  inconsistentRateData: boolean;
  contributionTypes: ContributionType[];
};

type DentalPlan = Plan & {
  benefitDetails: DentalBenefits;
  planRates: PlanRates;
};

type VisionPlan = Plan & {
  benefitDetails: VisionBenefits;
  planRates: PlanRates;
};

type StdPlan = Plan & {
  benefitDetails: STDBenefits;
  planRates: PlanRates;
};

type LtdPlan = Plan & {
  benefitDetails: LTDBenefits;
  planRates: PlanRates;
};

type BasicLifePlan = Plan & {
  benefitDetails: BasicLifeBenefits;
  planRates: BasicLifeRate;
};

type VoluntaryLifePlan = Plan & {
  benefitDetails: VoluntaryLifeBenefits;
  planRates: VolLifeRate;
};

type CriticalIllnessPlan = Plan & {
  benefitDetails: CriticalIllnessBenefits;
  planRates: CriticalIllnessRate | AgeBandedTieredRate;
};

type HospitalIndemnityPlan = Plan & {
  benefitDetails: HospitalIndemnityBenefits;
  planRates: PlanRates;
};

type AccidentPlan = Plan & {
  benefitDetails: AccidentBenefits;
  planRates: PlanRates;
};

type ContributionType = {
  class: string;
  contributionType: string;
};

type GeneralInformation = {
  rateGuarantee: string;
  originalEffectiveDate: string; // format: date
  mostRecentEffectiveDate: string; // format: date
  renewalDate: string; // format: date
  benefitClasses: string; // A concatenated representation of the benefit classes that this plan applies to.
  networkProvider: string;
  fundingType: string;
  participationRequirement: string;
};

type DentalNetworkBenefitDetails = {
  individualDeductible: string;
  familyDeductible: string;
  orthoMaximum: string;
  preventativeType1: string;
  basicType2: string;
  majorType3: string;
  orthoType4: string;
  annualMaximum: string;
  orthoAgeLimit: string;
  waitingPeriods: string;
  endodontics: string;
  periodonticsSurgical: string;
  periodonticsNonSurgical: string;
  oralSurgerySimple: string;
  oralSurgeryComplex: string;
  implants: string;
  sealants: string;
  spaceMaintainers: string;
  generalAnesthesia: string;
  compositeWhiteFillings: string;
};

type DhmoBenefitDetail = {
  adaCode: string;
  serviceName: string;
  memberCost: string;
};

type DentalBenefits = {
  inNetwork: DentalNetworkBenefitDetails;
  outOfNetwork: DentalNetworkBenefitDetails;
  isDeductibleWaivedPreventative: string;
  deductibleDuration: string;
  maximumRollover: string;
  outOfNetworkReimbursement: string;
  missingToothExclusion: string;
  dependentAgeLimit: string;
  annualOpenEnrollment: string;
  dhmoBenefits: DhmoBenefitDetail[];
};

type STDBenefits = {
  benefitAmount: string;
  maximumAmount: string;
  benefitDuration: string;
  eliminationPeriodAccident: string;
  eliminationPeriodIllness: string;
  preExistingCondition: string;
  definitionOfDisability: string;
  definitionOfEarnings: string;
  isFicaMatchIncluded: string;
  isFicaMatchBillable: string;
};

type LTDBenefits = {
  benefitDescription: string;
  maximumAmount: string;
  definitionOfDisability: string;
  eliminationPeriod: string;
  benefitDuration: string;
  specialConditionsLimitations: string;
  mentalIllness: string;
  substanceAbuse: string;
  preExistingCondition: string;
  rehab: string;
  guaranteeIssue: string;
  gainfulEarningsTestOwnOccupation: string;
  gainfulEarningsTestAnyOccupation: string;
  definitionOfEarnings: string;
  isColaIncluded: string;
  isConversionIncluded: string;
  isWaiverOfPremiumIncluded: string;
};

type VisionNetworkBenefitDetails = {
  exam: string;
  materials: string;
  singleLenses: string;
  bifocalLenses: string;
  trifocalLenses: string;
  frames: string;
  framesDiscounts: string;
  contactsElective: string;
  contactsMedicallyNecessary: string;
  lenticularLenses: string;
  standardProgressive: string;
  contactLensExam: string;
};

type VisionBenefits = {
  networkName: string;
  inNetwork: VisionNetworkBenefitDetails;
  outOfNetwork: VisionNetworkBenefitDetails;
  examFrequency: string;
  framesFrequency: string;
  lensesFrequency: string;
  annualOpenEnrollment: string;
};

type BasicLifeBenefits = {
  employeeBenefit: string;
  dependentBenefits: BasicLifeDependentBenefits;
  maximumAmount: string;
  portability: string;
  guaranteeIssue: string;
  ageReductionSchedule: string;
  isAdndIncluded: string;
  definitionOfEarnings: string;
  isConversionIncluded: string;
  isAcceleratedDeathBenefitIncluded: string;
  isWaiverOfPremiumIncluded: string;
};

type BasicLifeDependentBenefits = {
  spouse: string;
  child: string;
};

type VoluntaryLifeBenefitDetails = {
  benefitDescription: string;
  benefitMaximum: string;
  amountNotToExceed: string;
  guaranteeIssue: string;
  limitingAge: string;
  studentStatus: string;
  adndIncluded: string;
  adndTiedToLifeElection: string;
  portability: string;
  ageReductionSchedule: string;
  definitionOfEarnings: string;
  isConversionIncluded: string;
  isAcceleratedDeathBenefitIncluded: string;
  isWaiverOfPremiumIncluded: string;
  annualOpenEnrollment: string;
};

type VoluntaryLifeBenefits = {
  employee: VoluntaryLifeBenefitDetails;
  spouse: VoluntaryLifeBenefitDetails;
  child: VoluntaryLifeBenefitDetails;
};

type HospitalIndemnityBenefits = {
  hospitalAdmission: string;
  icuAdmission: string;
  hospitalConfinement: string;
  icuConfinement: string;
  preExistingCondition: string;
  portability: string;
  wellness: string;
  annualOpenEnrollment: string;
};

type AccidentBenefits = {
  coverageType: "On And Off Job" | "Off Job" | "Not Disclosed";
  hospitalAdmission: string;
  icuAdmission: string;
  dailyHospitalConfinement: string;
  icuConfinement: string;
  hospitalConfinementDurationMax: string;
  icuConfinementDurationMax: string;
  ambulanceAir: string;
  ambulanceGround: string;
  emergencyTreatment: string;
  urgentCare: string;
  initialPhysicianOfficeVisit: string;
  followUpPhysicianOfficeVisit: string;
  dislocations: string;
  fractures: string;
  coma: string;
  lacerations: string;
  paralysis: string;
  burnsSkinGraft: string;
  thicknessBurns: string;
  enhancedBenefitOrganizedSportsRelatedAccident: string;
  portability: string;
  wellness: string;
  ageReductions: string;
  isAdndIncluded: string;
  isChildOrganizedSportsEnhancement: string;
  majorDiagnosticExamXray: string;
  majorDiagnosticExamMri: string;
  annualOpenEnrollment: string;
};

type CriticalIllnessBenefitMember = {
  benefitAmount: string;
  benefitMaximum: string;
  guaranteeIssue: string;
};

type CriticalIllnessBenefits = {
  employee: CriticalIllnessBenefitMember;
  spouse: CriticalIllnessBenefitMember;
  child: {
    benefitAmount: string;
  };
  invasiveMalignantCancer: string;
  category1Vascular: string;
  organKidneyFailure: string;
  renalFailure: string;
  maximumPayout: string;
  occurrenceOfDiffIllnessCoverage: string;
  occurrenceOfDiffIllnessWaitingPeriod: string;
  additionalOccurrenceOfSameIllnessCoverage: string;
  additionalOccurrenceOfSameIllnessWaitingPeriod: string;
  ageReduction: string;
  preExistingCondition: string;
  portability: string;
  wellness: string;
  ageBasis: "Attained Age" | "Issue Age" | "Not Disclosed";
  invasiveCancer: string;
  malignantCancer: string;
  nonInvasiveCancer: string;
  category1VascularHeartAttack: string;
  category1VascularStroke: string;
  annualOpenEnrollment: string;
};

type CompositeRate = {
  type: typeof RATE_TYPES.COMPOSITE;
  tiers: {
    employee: {
      label: string;
      rate: string;
    };
    employeeDependent: {
      label: string;
      rate: string;
    };
    employee2Dependent: {
      label: string;
      rate: string;
    };
    employeeSpouse: {
      label: string;
      rate: string;
    };
    spouseOnly: {
      label: string;
      rate: string;
    };
    spouseChildren: {
      label: string;
      rate: string;
    };
    spouseDependent: {
      label: string;
      rate: string;
    };
    employeeChildren: {
      label: string;
      rate: string;
    };
    childOnly: {
      label: string;
      rate: string;
    };
    employeeFamily: {
      label: string;
      rate: string;
    };
  };
  rateGuarantee: string;
  rateUnit: "per $10" | "per $100" | "per $1000";
};

type FlatRate = {
  value: string;
  type: typeof RATE_TYPES.FLAT;
  fundingType: "ASO Fee" | "ATP Fee" | null;
  pepm: string;
  rateGuarantee: string;
  rateUnit: string;
};

type TierBanded = {
  name: string;
  value: string;
};

type AgeBandedRate = {
  type: typeof RATE_TYPES.AGE_BANDED;
  tiers: TierBanded[];
  rateUnit: string;
};

type BasicLifeRate = {
  type: typeof RATE_TYPES.BASIC_LIFE;
  volume: string;
  basicLifeRate: string;
  adndRate: string;
  rateUnit: string;
  rateGuarantee: string;
  dependentRate: string;
  dependentUnit: string;
  spouseRate: string;
  childRate: string;
  ageBandedRate: AgeBandedRate;
};

type VolLifeRate = {
  type: typeof RATE_TYPES.VOLUNTARY_LIFE;
  childLifeRate: string;
  childLifeAdndRate: string;
  employeeAdndRate: string;
  spouseAdndRate: string;
  rateGuarantee: string;
  employee: AgeBandedRate | AgeBandedTobaccoRate;
  spouse: AgeBandedRate | AgeBandedTobaccoRate;
};

type CriticalIllnessRate = {
  type: typeof RATE_TYPES.CRITICAL_ILLNESS;
  employee: CriticalIllnessAgeRate;
  spouse: CriticalIllnessAgeRate | null;
  child: string;
  childRateUnit: string;
  riderCharge: string;
};

type CriticalIllnessAgeRate = {
  ageTiers: CriticalIllnessAgeTiers[];
  rateUnit: string;
};

type CriticalIllnessAgeTiers = {
  age: string;
  rate: string;
  isTobacco: boolean | null;
};

type TierGender = {
  name: string;
  value: string; // format: currency
};

type AgeBandedGenderRate = {
  type: typeof RATE_TYPES.AGE_BANDED_GENDER;
  male: {
    tiers: TierGender[];
  };
  female: {
    tiers: TierGender[];
  };
  rateGuarantee: string;
  rateUnit: string;
};

type TierTobacco = {
  name: string;
  tobacco: string; // format: currency
  nonTobacco: string; // format: currency
};

type AgeBandedTobaccoRate = {
  type: typeof RATE_TYPES.AGE_BANDED_TOBACCO;
  tiers: TierTobacco[];
  rateGuarantee: string;
  rateUnit: string;
};

type AgeBandedTieredRate = {
  type: typeof RATE_TYPES.AGE_BANDED_TIERED;
  tiers: AgeBandedTiered;
  rateUnit: string;
};

type AgeBandedTiered = {
  employee: AgeBandedRate;
  employeeDependent: AgeBandedRate;
  employee2Dependents: AgeBandedRate;
  employeeSpouse: AgeBandedRate;
  spouseOnly: AgeBandedRate;
  spouseChildren: AgeBandedRate;
  spouseDependent: AgeBandedRate;
  employeeChildren: AgeBandedRate;
  childOnly: AgeBandedRate;
  employeeFamily: AgeBandedRate;
};

type Policy = {
  carrierName: string;
  groupInformation: GroupInformation;
  groupContacts: GroupContact[];
  billing: Billing[];
  benefitClasses: BenefitClass[];
  dental: DentalCoverage | null;
  vision: VisionCoverage | null;
  medical: MedicalCoverage | null;
  std: StdCoverage | null;
  ltd: LtdCoverage | null;
  basicLife: BasicLifeCoverage | null;
  volLife: VoluntaryLifeCoverage | null;
  accident: AccidentCoverage | null;
  hospitalIndemnity: HospitalIndemnityCoverage | null;
  criticalIllness: CriticalIllnessCoverage | null;
};

type Proposal = {
  id: string;
  rfpId: string;
  identifier: string;
  carrierIssuedIdentifier: string;
  carrierId: string;
  carrierName: string;
  dental: DentalCoverage | null;
  vision: VisionCoverage | null;
  medical: MedicalCoverage | null;
  std: StdCoverage | null;
  ltd: LtdCoverage | null;
  basicLife: BasicLifeCoverage | null;
  volLife: VoluntaryLifeCoverage | null;
  accident: AccidentCoverage | null;
  hospitalIndemnity: HospitalIndemnityCoverage | null;
  criticalIllness: CriticalIllnessCoverage | null;
};

const COVERAGE_TYPE = {
  DENTAL: "dental",
  MEDICAL: "medical",
  VISION: "vision",
  STD: "std",
  LTD: "ltd",
  BASIC_LIFE: "basicLife",
  VOLUNTARY_LIFE: "volLife",
  ACCIDENT: "accident",
  HOSPITAL_INDEMNITY: "hospitalIndemnity",
  CRITICAL_ILLNESS: "criticalIllness",
} as const;

type MedicalPlan = {
  planType: PlanType;
  basics: MedicalPlanBasics;
  contributionTypes: ContributionType;
  deductibleAndOutOfPocketDetails: MedicalPlanDeductibleAndOutOfPocketDetails | null;
  services: MedicalPlanServices | null;
  planRates: PlanRates;
  inconsistentRateData: boolean;
};

type MedicalPlanBasics = {
  cobraEligible: string;
  effectiveDate: string;
  metallicLevel: string;
  planIdentifier: string;
  name: string;
  fundingType: string;
  terminationRule: string;
};

type MedicalPlanDeductibleAndOutOfPocketDetails = {
  inNetwork: MedicalDeductibleNetworkDetails;
  outOfNetwork: MedicalDeductibleNetworkDetails;
  isDeductibleCalculatedIntoOutOfPocketMax: string;
  deductibleTimePeriod: string;
};

type MedicalDeductibleNetworkDetails = {
  familyDeductible: string;
  familyOutOfPocketMax: string;
  individualDeductible: string;
  individualOutOfPocketMax: string;
};

type MedicalPlanServices = {
  inNetwork: MedicalNetworkBenefitDetails;
  outOfNetwork: MedicalNetworkBenefitDetails;
};

type MedicalNetworkBenefitDetails = {
  accupuncture: string;
  chiropractic: string;
  diagnosticLaboratory: string;
  emergencyRoom: string;
  inpatientCare: string;
  majorComplexXRay: string;
  officeVisitPrimaryCare: string;
  officeVisitSpecialist: string;
  outpatientCare: string;
  preventativeCare: string;
  urgentCareVisit: string;
  virtualCareVisit: string;
};

export {
  Policy,
  Proposal,
  GroupInformation,
  GroupContact,
  Billing,
  BenefitClass,
  Plan,
  Coverage,
  DentalCoverage,
  DentalPlan,
  VisionCoverage,
  VisionPlan,
  BasicLifeCoverage,
  BasicLifePlan,
  StdCoverage,
  StdPlan,
  LtdCoverage,
  LtdPlan,
  VoluntaryLifeCoverage,
  VoluntaryLifePlan,
  AccidentCoverage,
  AccidentPlan,
  HospitalIndemnityCoverage,
  HospitalIndemnityPlan,
  CriticalIllnessCoverage,
  CriticalIllnessPlan,
  CriticalIllnessRate,
  CriticalIllnessAgeTiers,
  COVERAGE_TYPE,
  FlatRate,
  AgeBandedRate,
  AgeBandedGenderRate,
  AgeBandedTobaccoRate,
  CompositeRate,
  BasicLifeRate,
  VolLifeRate,
  AgeBandedTieredRate,
  PlanRates,
  TierBanded,
  TierGender,
  TierTobacco,
  DentalNetworkBenefitDetails,
  DentalBenefits,
  STDBenefits,
  LTDBenefits,
  VisionBenefits,
  BasicLifeBenefits,
  GeneralInformation,
  ContributionType,
  VoluntaryLifeBenefitDetails,
  VoluntaryLifeBenefits,
  HospitalIndemnityBenefits,
  AccidentBenefits,
  CriticalIllnessBenefits,
  MedicalCoverage,
  MedicalPlan,
  MedicalPlanBasics,
  MedicalPlanDeductibleAndOutOfPocketDetails,
  MedicalDeductibleNetworkDetails,
  MedicalPlanServices,
  MedicalNetworkBenefitDetails,
};
